import { ref, computed, watch, toValue, type MaybeRefOrGetter } from "vue";
import { useDocumentVisibility, useFetch, useIntervalFn } from "@vueuse/core";

export type BankIdMode = "identification" | "signing";

export interface BankIdInitResponse {
  token: string;
  qr?: string;
  auto_start_token?: string;
}

export interface BankIdCreateSigningTokenResponse {
  token: string;
}

export interface BankIdPollResponse {
  token: string;
  status: "pending" | "completed" | "failed";
  qr?: string;
  hint_code?: string;
}

export interface ADAApiErrorResponse {
  _status: "ERR";
  _error: {
    code: number;
    message: string;
  };

  // This is present on BankID responses
  _locale_message?: string;
}

export function isADAApiErrorResponse(x: unknown): x is ADAApiErrorResponse {
  return typeof x === "object" && x != null && "_error" in x;
}

export interface BankIdOptions<SignData = unknown> {
  signing?: {
    data: SignData;
    message?: string;
  };
  endpointOverrides?: {
    init?: string;
    poll?: string;
    cancel?: string;
    createSigningToken?: string;
  };
}

const baseURL = import.meta.env.VITE_APP_API_ROOT;

// This composable is used for both identification and signing modes.
export function useBankId<SignData = unknown>(
  mode: BankIdMode,
  options: MaybeRefOrGetter<BankIdOptions<SignData>>,
) {
  const resolvedOptions = computed(() => toValue(options) ?? {});
  const signing = computed(() => resolvedOptions.value.signing);
  const endpointOverrides = computed(() => resolvedOptions.value.endpointOverrides);
  const isManuallyPaused = ref(false);

  const defaultInit = `authenticates/mobile-bankid-init/${import.meta.env.VITE_APP_NAME}`;
  const defaultPoll = "authenticates/mobile-bankid";
  const defaultCancel = "authenticates/mobile-bankid";
  const defaultCreateSigning = "authenticates/mobile-bankid";

  const createSigningTokenUrl = computed(() => {
    const endpoint = endpointOverrides.value?.createSigningToken ?? defaultCreateSigning;
    return new URL(endpoint, baseURL);
  });

  const createSigningTokenUrlHref = computed(() => createSigningTokenUrl.value?.href ?? "");
  const createSigningTokenRequest = useFetch<BankIdCreateSigningTokenResponse>(
    createSigningTokenUrlHref,
    {
      updateDataOnError: true,
      immediate: false,
    },
  )
    .post(computed(() => signing.value?.data))
    .json<BankIdCreateSigningTokenResponse>();

  const createSigningData = computed(() =>
    isADAApiErrorResponse(createSigningTokenRequest.data.value)
      ? undefined
      : createSigningTokenRequest.data.value,
  );
  const createError = computed(() =>
    isADAApiErrorResponse(createSigningTokenRequest.data.value)
      ? createSigningTokenRequest.data.value
      : undefined,
  );

  const signingToken = ref<string | undefined>(createSigningData.value?.token);

  const initUrl = computed(() => {
    const endpoint = endpointOverrides.value?.init ?? defaultInit;
    const resolvedUrl = signingToken.value
      ? endpoint.replace("{signing_token}", signingToken.value)
      : endpoint;

    return resolvedUrl.includes("{signing_token}") ? null : new URL(resolvedUrl, baseURL);
  });
  const initUrlHref = computed(() => initUrl.value?.href ?? "");
  const initRequest = useFetch<BankIdInitResponse>(initUrlHref, {
    updateDataOnError: true,
    immediate: false,
  })
    .post(
      computed(() => ({
        signing_token: signingToken.value,
        user_visible_data: signing.value?.message,
      })),
    )
    .json<BankIdInitResponse>();

  const initData = computed(() =>
    isADAApiErrorResponse(initRequest.data.value) ? undefined : initRequest.data.value,
  );
  const initError = computed(() =>
    isADAApiErrorResponse(initRequest.data.value) ? initRequest.data.value : undefined,
  );
  const hasInitData = computed(() => !!initData.value && !initError.value);

  const pollUrl = computed(() => {
    const endpoint = endpointOverrides.value?.poll ?? defaultPoll;
    const resolvedUrl = signingToken.value
      ? endpoint.replace("{signing_token}", signingToken.value)
      : endpoint;

    return resolvedUrl.includes("{signing_token}")
      ? null
      : new URL(resolvedUrl, import.meta.env.VITE_APP_API_ROOT);
  });

  const pollUrlHref = computed(() => pollUrl.value?.href ?? "");
  const pollRequest = useFetch<BankIdPollResponse>(pollUrlHref, {
    updateDataOnError: true,
    timeout: 5_000,
    immediate: false,
    beforeFetch(ctx) {
      if (signingToken.value == null) {
        ctx.cancel();
        return;
      }

      ctx.options.headers = {
        "x-api-key": signingToken.value,
        "cache-control": "no-cache",
      };
    },
  })
    .get()
    .json<BankIdPollResponse>();

  const pollData = computed(() =>
    isADAApiErrorResponse(pollRequest.data.value) ? undefined : pollRequest.data.value,
  );
  const pollError = computed(() =>
    isADAApiErrorResponse(pollRequest.data.value) ? pollRequest.data.value : undefined,
  );
  const pollInterval = useIntervalFn(() => {
    if (hasInitData.value && signingToken.value) {
      void pollRequest.execute();
    }
  }, 1_000);

  const initDataToken = computed(() => initData.value?.token);
  const pollDataToken = computed(() => pollData.value?.token);

  const preAuthToken = computed(() => pollData.value?.token ?? initData.value?.token);
  const status = computed(() => pollData.value?.status);
  const error = computed(() => createError.value ?? pollError.value ?? initError.value);
  const documentVisibility = useDocumentVisibility();

  watch(
    [
      () => createSigningData.value?.token,
      () => pollData.value?.token,
      () => initData.value?.token,
    ],
    ([createToken, pollToken, initToken]) => {
      signingToken.value = createToken ?? pollToken ?? initToken;
    },
    { immediate: true },
  );

  watch(
    [signingToken, documentVisibility, status, error, hasInitData],
    ([token, visibility, status, error]) => {
      if (
        !isManuallyPaused.value &&
        token != null &&
        hasInitData &&
        visibility === "visible" &&
        (status === "pending" || status == null) &&
        error == null
      ) {
        pollInterval.resume();
      } else {
        pollInterval.pause();
      }
    },
  );

  watch([pollDataToken, initDataToken], ([newPollToken, newInitToken]) => {
    if (newPollToken) {
      signingToken.value = newPollToken;
    } else if (newInitToken) {
      signingToken.value = newInitToken;
    }
  });

  async function start() {
    reset();
    isManuallyPaused.value = false;
    await createSigningTokenRequest.execute();
    if (signingToken.value) {
      return initRequest.execute();
    }
  }

  function reset() {
    if (initRequest.canAbort) initRequest.abort();
    if (pollRequest.canAbort) pollRequest.abort();

    initRequest.data.value = null;
    pollRequest.data.value = null;
    isManuallyPaused.value = false;
  }

  const cancelUrl = computed(() => {
    const endpoint = endpointOverrides.value?.cancel ?? defaultCancel;
    const resolvedUrl = signingToken.value
      ? endpoint.replace("{signing_token}", signingToken.value)
      : endpoint;

    return resolvedUrl.includes("{signing_token}")
      ? null
      : new URL(resolvedUrl, import.meta.env.VITE_APP_API_ROOT);
  });
  const cancelUrlHref = computed(() => cancelUrl.value?.href ?? "");
  const cancelRequest = useFetch(cancelUrlHref, {
    immediate: false,
    onFetchError(ctx) {
      console.error("Error during BankID cancel request:", ctx.error);
      return ctx;
    },
    beforeFetch(ctx) {
      if (preAuthToken.value == null) {
        ctx.cancel();
        return;
      }
      ctx.options.headers = {
        "x-api-key": preAuthToken.value,
        "cache-control": "no-cache",
      };
    },
  })
    .delete()
    .json();

  const cancelLogin = async () => {
    if (cancelUrlHref.value) await cancelRequest.execute();
  };

  function prepareForRedirect() {
    isManuallyPaused.value = true;
    pollInterval.pause();
    if (signingToken.value) {
      localStorage.setItem("bankid_signing_token", signingToken.value);
    }
  }

  async function ensureTokenOnLoad() {
    const storedToken = localStorage.getItem("bankid_signing_token");
    if (storedToken) {
      signingToken.value = storedToken;
      localStorage.removeItem("bankid_signing_token");
      await pollRequest.execute();
    }
  }
  void ensureTokenOnLoad();

  return {
    start,
    reset,
    cancelLogin,
    prepareForRedirect,
    qrBase64: computed(() => pollData.value?.qr ?? initData.value?.qr),
    autostartToken: computed(() => initData.value?.auto_start_token),
    status,
    hintCode: computed(() => pollData.value?.hint_code),
    error,
  };
}
